import { createContext, useContext } from 'react';
import type { PropsWithChildren } from 'react';
import type { UseFormReturn, FieldValues } from 'react-hook-form';

// 全局 FormInstance 管理器
class FormInstanceManager {
    private instances = new Map<string, UseFormReturn<any>>();
    private defaultInstance: UseFormReturn<any> | null = null;

    register(instance: UseFormReturn<any>, key?: string) {
        if (key) {
            this.instances.set(key, instance);
        } else {
            this.defaultInstance = instance;
        }
    }

    get(key?: string): UseFormReturn<any> | null {
        if (key) {
            return this.instances.get(key) || null;
        }
        return this.defaultInstance;
    }

    unregister(key?: string) {
        if (key) {
            this.instances.delete(key);
        } else {
            this.defaultInstance = null;
        }
    }

    clear() {
        this.instances.clear();
        this.defaultInstance = null;
    }
}

const formManager = new FormInstanceManager();

interface FormProviderProps<TFieldValues extends FieldValues = FieldValues> extends PropsWithChildren {
    value: UseFormReturn<TFieldValues>;
}

const FormContext = createContext<UseFormReturn<FieldValues> | null>(null);

export function useFormContext<TFieldValues extends FieldValues = FieldValues>(): UseFormReturn<TFieldValues> {
    const context = useContext(FormContext);
    if (!context) {
        throw new Error('useFormContext must be used within a FormProvider');
    }
    return context as UseFormReturn<TFieldValues>;
}

// 用于检测是否在 FormProvider 内部的 hook
export function useFormContextOptional<TFieldValues extends FieldValues = FieldValues>(): UseFormReturn<TFieldValues> | null {
    const context = useContext(FormContext);
    return context as UseFormReturn<TFieldValues> | null;
}

// 检测是否在 FormProvider 内部
export function useIsInsideFormProvider(): boolean {
    const context = useContext(FormContext);
    return context !== null;
}

// 获取可用的 FormInstance（按优先级查找）
export function useAvailableFormInstance<TFieldValues extends FieldValues = FieldValues>(): UseFormReturn<TFieldValues> | null {
    // 优先级：FormProvider context > 全局默认实例
    const context = useContext(FormContext);
    if (context) {
        return context as UseFormReturn<TFieldValues>;
    }
    
    return formManager.get() as UseFormReturn<TFieldValues> | null;
}

// 注册全局 FormInstance
export function registerFormInstance(instance: UseFormReturn<any>, key?: string) {
    formManager.register(instance, key);
}

// 注销全局 FormInstance
export function unregisterFormInstance(key?: string) {
    formManager.unregister(key);
}

// 获取指定的全局 FormInstance
export function getFormInstance(key?: string): UseFormReturn<any> | null {
    return formManager.get(key);
}

export const FormProvider = <TFieldValues extends FieldValues = FieldValues>({ value, children }: FormProviderProps<TFieldValues>) => {
    return <FormContext.Provider value={value as UseFormReturn<FieldValues>}>{children}</FormContext.Provider>;
};
